import type {
  CdaAct,
  CdaAddress,
  CdaCode,
  CdaConsumable,
  CdaDocument,
  CdaDoseQuantity,
  CdaEncounter,
  CdaEntity,
  CdaEntryRelationship,
  CdaHumanName,
  CdaId,
  CdaLocation,
  CdaObservation,
  CdaOrganization,
  CdaOrganizer,
  CdaParticipant,
  CdaPerformer,
  CdaPerson,
  CdaPrecondition,
  CdaProcedure,
  CdaQuantity,
  CdaSpecimen,
  CdaSubstanceAdministration,
  CdaSupply,
  CdaTelecom,
  CdaTextReference,
  CdaTime,
} from '@ctw/shared/content/document-viewer/_internal/parsers/types';
import { compact, isEmpty, kebabCase, trim } from 'lodash-es';

interface AnyJsonObj {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  [key: string]: any;
}

export class CdaParser {
  private readonly xml: string;
  private readonly rawJson: AnyJsonObj;
  private readonly json: CdaDocument;

  public constructor(xml: string) {
    this.xml = trim(xml);
    this.rawJson = this.parseToJson(this.xml);
    this.json = this.mapToDocumentSchema(this.rawJson);
  }

  public toRawJsonString({ pretty = false }: { pretty: boolean } = { pretty: false }): string {
    if (pretty) {
      return JSON.stringify(this.rawJson, null, 2);
    }

    return JSON.stringify(this.rawJson);
  }

  public toRawJson(): CdaDocument {
    return this.rawJson;
  }

  public toDocumentJsonString({ pretty = false }: { pretty: boolean } = { pretty: false }): string {
    if (pretty) {
      return JSON.stringify(this.json, null, 2);
    }

    return JSON.stringify(this.json);
  }

  public toDocumentJson(): CdaDocument {
    return this.json;
  }

  public toXml({ pretty = false }: { pretty: boolean } = { pretty: false }): string {
    if (!pretty) {
      return this.xml;
    }

    // https://stackoverflow.com/a/47317538
    const xmlDoc = new DOMParser().parseFromString(this.xml, 'application/xml');
    const xsltDoc = new DOMParser().parseFromString(
      [
        // describes how we want to modify the XML - indent everything
        '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform">',
        '  <xsl:strip-space elements="*"/>',
        '  <xsl:template match="para[content-style][not(text())]">', // change to just text() to strip space in text nodes
        '    <xsl:value-of select="normalize-space(.)"/>',
        '  </xsl:template>',
        '  <xsl:template match="node()|@*">',
        '    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>',
        '  </xsl:template>',
        '  <xsl:output indent="yes"/>',
        '</xsl:stylesheet>',
      ].join('\n'),
      'application/xml',
    );

    const xsltProcessor = new XSLTProcessor();
    xsltProcessor.importStylesheet(xsltDoc);
    const resultDoc = xsltProcessor.transformToDocument(xmlDoc);

    return new XMLSerializer().serializeToString(resultDoc);
  }

  private parseToJson(xml: string): AnyJsonObj {
    const dom = new DOMParser().parseFromString(xml, 'application/xml');
    return { [dom.documentElement.nodeName]: this.xmlToJson(dom.documentElement) };
  }

  private mapToDocumentSchema(json: AnyJsonObj): CdaDocument {
    const contentComponents: Array<AnyJsonObj> = Array.isArray(json.ClinicalDocument?.component)
      ? json.ClinicalDocument?.component.reduce((acc: Array<AnyJsonObj>, component: AnyJsonObj) => {
          acc.push(component.structuredBody?.component);
          return acc;
        }, [])
      : json.ClinicalDocument?.component?.structuredBody?.component;

    try {
      const parsed = this.recursivelyCompactObject({
        metadata: {
          xml: {
            title: this.parseAndFlattenTextReference(json.ClinicalDocument?.title),
            namespaceUrl: json.ClinicalDocument?.['@xmlns:xsi'],
            namespace: json.ClinicalDocument?.['@xmlns'],
            schemaLocation: json.ClinicalDocument?.['@xsi:schemaLocation'],
          },
          document: {
            id: this.parseId(json.ClinicalDocument?.id),
            versionNumber: json.ClinicalDocument?.versionNumber?.['@value'],
            effectiveTime: this.parseTime(json.ClinicalDocument?.effectiveTime),
            realmCode: this.parseCode(json.ClinicalDocument?.realmCode),
            templateIds: this.yieldArray(json.ClinicalDocument?.templateId, (array) =>
              array.map((templateId: AnyJsonObj) => this.parseId(templateId)),
            ),
            typeId: this.parseId(json.ClinicalDocument?.typeId),
            setId: this.parseId(json.ClinicalDocument?.setId),
            code: this.parseCode(json.ClinicalDocument?.code),
            languageCode: json.ClinicalDocument?.languageCode?.['@code'],
            recordTarget: this.yieldIfPresent(
              json.ClinicalDocument?.recordTarget,
              (recordTarget) => ({
                patientRole: {
                  ids: this.yieldArray(recordTarget.patientRole.id, (array) =>
                    array.map((id: AnyJsonObj) => this.parseId(id)),
                  ),
                  telecom: this.parseTelecom(recordTarget.patientRole.telecom),
                  address: this.parseAddress(recordTarget.patientRole.addr),
                  providerOrganization: this.parseOrganization(
                    recordTarget.patientRole.providerOrganization,
                  ),
                },
                patient: this.parsePerson(recordTarget.patientRole.patient),
              }),
            ),
            /* This commented block parses data we don't currently use.
             * Until we need it, it is commented out to avoid unnecessary
             * time and memory usage when parsing.
             *
            authenticator: this.parseEntity(json.ClinicalDocument?.authenticator),
            legalAuthenticator: this.parseEntity(json.ClinicalDocument?.legalAuthenticator),
            informationRecipient: this.yieldIfPresent(
              json.ClinicalDocument?.informationRecipient,
              (informationRecipient) => ({
                intendedRecipient: this.yieldIfPresent(
                  informationRecipient.intendedRecipient,
                  (intendedRecipient) => ({
                    informationRecipient: this.parsePerson(intendedRecipient.informationRecipient),
                    receivedOrganization: this.parseOrganization(
                      intendedRecipient.receivedOrganization,
                    ),
                  }),
                ),
              }),
            ),
            informants: {
              assignedEntities: this.yieldArray(json.ClinicalDocument?.informant, (array) =>
                array
                  .filter((informant: AnyJsonObj) => Object.hasOwn(informant, 'assignedEntity'))
                  .map((informant: AnyJsonObj) => this.parseEntity(informant.assignedEntity)),
              ),
              relatedEntities: this.yieldArray(json.ClinicalDocument?.informant, (array) =>
                array
                  .filter((informant: AnyJsonObj) => Object.hasOwn(informant, 'relatedEntity'))
                  .map((informant: AnyJsonObj) => this.parseEntity(informant.relatedEntity)),
              ),
            },
            documentationOf: this.yieldIfPresent(
              json.ClinicalDocument?.documentationOf,
              (documentationOf) => ({
                typeCode: documentationOf['@typeCode'],
                serviceEvent: this.yieldIfPresent(documentationOf.serviceEvent, (serviceEvent) => ({
                  classCode: serviceEvent['@classCode'],
                  effectiveTime: this.parseTime(serviceEvent.effectiveTime),
                  performers: this.yieldArray(serviceEvent.performer, (array) =>
                    array.map((performer: AnyJsonObj) => this.parsePerformer(performer)),
                  ),
                })),
              }),
            ),
            dataEnterer: this.yieldIfPresent(json.ClinicalDocument?.dataEnterer, (dataEnterer) => ({
              assignedEntity: this.parseEntity(dataEnterer.assignedEntity),
            })),
            custodian: this.parseOrganization(
              json.ClinicalDocument?.custodian?.assignedCustodian?.representedCustodianOrganization,
            ),
            confidentialityCode: this.parseCode(json.ClinicalDocument?.confidentialityCode),
            author: this.parseAuthor(json.ClinicalDocument?.author),
            componentOf: this.yieldIfPresent(json.ClinicalDocument?.componentOf, (componentOf) => ({
              encompassingEncounter: this.parseEncounter(componentOf.encounter),
            })),
            */
          },
        },
        content: {
          sections: this.yieldArray(contentComponents, (array) =>
            array.map((section: AnyJsonObj) => {
              const id = trim(kebabCase(this.parseAndFlattenTextReference(section.section?.title)));
              const domId = `document-section-${id}`;
              const title = trim(this.parseAndFlattenTextReference(section.section?.title));

              if (isEmpty(id) || isEmpty(title) || isEmpty(domId)) {
                return undefined;
              }

              return {
                id,
                domId,
                title,
                index: 0,
                templateId: this.parseId(section.section?.templateId),
                code: this.parseCode(section.section?.code),
                /* This commented block parses data we don't currently use.
                   * Until we need it, it is commented out to avoid unnecessary
                   * time and memory usage when parsing.
                   *
                  entries: this.yieldArray(section.section?.entry, (array) =>
                    array.map((entry: AnyJsonObj) => ({
                      typeCode: entry['@typeCode'],
                      templateId: entry['@templateId'],
                      act: this.parseAct(entry.act),
                      procedure: this.parseProcedure(entry.procedure),
                      substanceAdministration: this.parseSubstanceAdministration(
                        entry.substanceAdministration,
                      ),
                      organizer: this.parseOrganizer(entry.organizer),
                      observation: this.parseObservation(entry.observation),
                      performer: this.parsePerformer(entry.performer),
                      specimen: this.parseSpecimen(entry.specimen),
                      encounter: this.parseEncounter(entry.encounter),
                      supply: this.parseSupply(entry.supply),
                    })),
                  ),
                  */
                html: section.section.text?.trim(),
              };
            }),
          )
            .filter(Boolean)
            .map((section, iii) => {
              if (section !== undefined) {
                section.index = iii;
              }
              return section;
            }),
        },
      }) as CdaDocument;

      if (
        isEmpty(parsed?.metadata?.xml?.namespace) &&
        isEmpty(parsed?.metadata?.document?.recordTarget)
      ) {
        return {};
      }

      return parsed;
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: <explanation>
      console.error(error);
      return {};
    }
  }

  private parseEncounter(encounter: AnyJsonObj | undefined): CdaEncounter | undefined {
    return (
      encounter && {
        id: this.parseId(encounter.id),
        code: this.parseCode(encounter.code),
        effectiveTime: this.parseTime(encounter.effectiveTime),
        location: this.parseLocation(encounter.location),
      }
    );
  }

  private parseLocation(location: AnyJsonObj | undefined): CdaLocation | undefined {
    return (
      location && {
        healthcareFacility: this.yieldIfPresent(
          location.healthcareFacility,
          (healthcareFacility) => ({
            id: this.parseId(healthcareFacility.id),
          }),
        ),
      }
    );
  }

  private parseProcedure(procedure: AnyJsonObj | undefined): CdaProcedure | undefined {
    return (
      procedure && {
        classCode: procedure['@classCode'],
        moodCode: procedure['@moodCode'],
        statusCode: procedure['@statusCode'],
        priorityCode: this.parseCode(procedure.priorityCode),
        targetSiteCode: this.parseCode(procedure.targetSiteCode),
        code: this.parseCode(procedure.code),
        effectiveTime: this.parseTime(procedure.effectiveTime),
        id: this.parseId(procedure.id),
        methodCode: this.parseCode(procedure.methodCode),
        participant: this.parseParticipant(procedure.participant),
      }
    );
  }

  private parseAct(act: AnyJsonObj | undefined): CdaAct | undefined {
    return (
      act && {
        classCode: act['@classCode'],
        moodCode: act['@moodCode'],
        priorityCode: this.parseCode(act.priorityCode),
        targetSiteCode: this.parseCode(act.targetSiteCode),
        code: this.parseCode(act.code),
        effectiveTime: this.parseTime(act.effectiveTime),
        id: this.parseId(act.id),
        templateId: this.parseId(act.templateId),
        statusCode: this.parseCode(act.statusCode),
        entryRelationships: this.yieldArray(act.entryRelationship, (array) =>
          array.map((entryRelationship) => this.parseEntryRelationship(entryRelationship)),
        ),
      }
    );
  }

  private parseEntryRelationship(
    entryRelationship: AnyJsonObj | undefined,
  ): CdaEntryRelationship | undefined {
    return (
      entryRelationship && {
        typeCode: entryRelationship['@typeCode'],
        inversionInd: entryRelationship['@inversionInd'],
        act: this.parseAct(entryRelationship.act),
        procedure: this.parseProcedure(entryRelationship.procedure),
        substanceAdministration: this.parseSubstanceAdministration(
          entryRelationship.substanceAdministration,
        ),
        organizer: this.parseOrganizer(entryRelationship.organizer),
        observation: this.parseObservation(entryRelationship.observation),
        performer: this.parsePerformer(entryRelationship.performer),
        specimen: this.parseSpecimen(entryRelationship.specimen),
        encounter: this.parseEncounter(entryRelationship.encounter),
        supply: this.parseSupply(entryRelationship.supply),
      }
    );
  }

  private parseSupply(supply: AnyJsonObj | undefined): CdaSupply | undefined {
    return (
      supply && {
        classCode: supply['@classCode'],
        moodCode: supply['@moodCode'],
        templateId: supply.templateId['@root'],
        id: this.parseId(supply.id),
        effectiveTime: this.parseTime(supply.effectiveTime),
        repeatNumber: this.parseQuantity(supply.repeatNumber),
        quantity: this.parseQuantity(supply.quantity),
        author: this.parseEntity(supply.author),
        entryRelationships: this.yieldArray(supply.entryRelationship, (array) =>
          array.map((entryRelationship: AnyJsonObj) =>
            this.parseEntryRelationship(entryRelationship),
          ),
        ),
      }
    );
  }

  private parseSpecimen(specimen: AnyJsonObj | undefined): CdaSpecimen | undefined {
    return (
      specimen && {
        typeCode: specimen['@typeCode'],
        specimenRole: this.yieldIfPresent(specimen.specimenRole, (specimenRole) => ({
          classCode: specimenRole['@classCode'],
          id: this.parseId(specimenRole.id),
          specimenPlayingEntity: this.parseEntity(specimenRole.specimenPlayingEntity),
          templateId: specimenRole.templateId['@root'],
        })),
        entryRelationships: this.yieldArray(specimen.entryRelationship, (array) =>
          array.map((entryRelationship: AnyJsonObj) =>
            this.parseEntryRelationship(entryRelationship),
          ),
        ),
      }
    );
  }

  private parseEntity(entity: AnyJsonObj | undefined): CdaEntity | undefined {
    return (
      entity && {
        classCode: entity['@classCode'],
        name: this.parseTextReference(entity.name),
        code: this.parseCode(entity.code),
        desc: this.parseTextReference(entity.desc),
        time: this.parseTime(entity.time),
        signatureCode: this.parseCode(entity.signatureCode),
        telecom: this.parseTelecom(entity.telecom),
        address: this.parseAddress(entity.addr),
        id: this.parseId(entity.id),
        assignedEntity: this.parseEntity(entity.assignedEntity),
        assignedPerson: this.parsePerson(entity.assignedPerson),
        representedOrganization: this.parseOrganization(entity.representedOrganization),
      }
    );
  }

  private parseSubstanceAdministration(
    substanceAdministration: AnyJsonObj | undefined,
  ): CdaSubstanceAdministration | undefined {
    return (
      substanceAdministration && {
        text: this.parseTextReference(substanceAdministration.text),
        id: this.parseId(substanceAdministration.id),
        templateId: this.parseId(substanceAdministration.templateId),
        negationInd: substanceAdministration['@negationInd'],
        statusCode: substanceAdministration['@statusCode'],
        priorityCode: this.parseCode(substanceAdministration.priorityCode),
        effectiveTime: this.parseTime(substanceAdministration.effectiveTime),
        targetSiteCode: this.parseCode(substanceAdministration.targetSiteCode),
        routeCode: this.parseCode(substanceAdministration.routeCode),
        rateQuantity: this.parseQuantity(substanceAdministration.rateQuantity),
        administrativeUnitCode: this.parseCode(substanceAdministration.administrativeUnitCode),
        consumable: this.parseConsumable(substanceAdministration.consumable),
        doseQuantity: this.parseDoseQuantity(substanceAdministration.doseQuantity),
        precondition: this.parsePrecondition(substanceAdministration.precondition),
        maxDoseQuantity: this.parseDoseQuantity(substanceAdministration.maxDoseQuantity),
        participant: this.parseParticipant(substanceAdministration.participant),
        performer: this.parsePerformer(substanceAdministration.performer),
        entryRelationships: this.yieldArray(substanceAdministration.entryRelationship, (array) =>
          array.map((entryRelationship: AnyJsonObj) =>
            this.parseEntryRelationship(entryRelationship),
          ),
        ),
      }
    );
  }

  private parseConsumable(consumable: AnyJsonObj | undefined): CdaConsumable | undefined {
    return (
      consumable && {
        manufacturedProduct: this.yieldIfPresent(
          consumable.manufacturedProduct,
          (manufacturedProduct) => ({
            id: this.parseId(manufacturedProduct.id),
            templateId: this.parseId(manufacturedProduct.templateId),
            manufacturedMaterial: this.yieldIfPresent(
              manufacturedProduct.manufacturedMaterial,
              (manufacturedMaterial) => ({
                code: this.parseCode(manufacturedMaterial),
              }),
            ),
            manufacturerOrganization: this.parseOrganization(
              manufacturedProduct.manufacturerOrganization,
            ),
          }),
        ),
        entryRelationships: this.yieldArray(consumable.entryRelationship, (array) =>
          array.map((entryRelationship: AnyJsonObj) =>
            this.parseEntryRelationship(entryRelationship),
          ),
        ),
      }
    );
  }

  private parsePrecondition(precondition: AnyJsonObj | undefined): CdaPrecondition | undefined {
    return (
      precondition && {
        typeCode: precondition['@typeCode'],
        templateId: this.parseId(precondition.templateId),
        criterion: this.yieldIfPresent(precondition.criterion, (criterion) => ({
          code: this.parseCode(criterion.code),
          value: this.parseCode(criterion.value),
        })),
      }
    );
  }

  private parsePerformer(performer: AnyJsonObj | undefined): CdaPerformer | undefined {
    return (
      performer && {
        typeCode: performer['@typeCode'],
        functionCode: this.parseCode(performer.functionCode),
        time: this.parseTime(performer.time),
        assignedEntity: this.parseEntity(performer.assignedEntity),
      }
    );
  }

  private parseParticipant(participant: AnyJsonObj | undefined): CdaParticipant | undefined {
    return (
      participant && {
        typeCode: participant['@typeCode'],
        time: this.parseTime(participant.time),
        participantRole: this.yieldIfPresent(participant.participantRole, (participantRole) => ({
          classCode: participantRole['@classCode'],
          templateId: this.parseId(participantRole.templateId),
          code: this.parseCode(participantRole.code),
          playingEntity: this.parseEntity(participantRole.playingEntity),
          scopingEntity: this.parseEntity(participantRole.scopingEntity),
        })),
      }
    );
  }

  private parseOrganizer(organizer: AnyJsonObj | undefined): CdaOrganizer | undefined {
    return (
      organizer && {
        id: this.parseId(organizer.id),
        templateId: this.parseId(organizer.templateId),
        statusCode: this.parseCode(organizer.statusCode),
        effectiveTime: this.parseTime(organizer.effectiveTime),
        code: this.parseCode(organizer.code),
        classCode: organizer['@classCode'],
        moodCode: organizer['@moodCode'],
        components: this.yieldArray(organizer.component, (array) =>
          array.map((component) => ({
            observation: this.parseObservation(component.observation),
          })),
        ),
      }
    );
  }

  private parseObservation(observation: AnyJsonObj | undefined): CdaObservation | undefined {
    return (
      observation && {
        classCode: observation['@classCode'],
        moodCode: observation['@moodCode'],
        code: this.parseCode(observation.code),
        effectiveTime: this.parseTime(observation.effectiveTime),
        id: this.parseId(observation.id),
        statusCode: this.parseCode(observation.statusCode),
        templateId: this.parseId(observation.templateId),
        interpretationCode: this.parseCode(observation.interpretationCode),
        methodCode: this.parseCode(observation.methodCode),
        targetSiteCode: this.parseCode(observation.targetSiteCode),
        text: this.parseTextReference(observation.text),
        author: this.yieldIfPresent(observation.author, (author) => ({
          time: this.parseTime(author.time),
          assignedAuthor: this.parsePerson(author.assignedAuthor),
        })),
        referenceRange: this.yieldIfPresent(observation.referenceRange, (referenceRange) => ({
          observationRange: this.yieldIfPresent(
            referenceRange.observationRange,
            (observationRange) => ({
              value: this.parseQuantity(observationRange.value),
            }),
          ),
        })),
        value: this.parseQuantity(observation.value),
      }
    );
  }

  private parsePerson(person: AnyJsonObj | undefined): CdaPerson | undefined {
    return (
      person && {
        id: this.parseId(person.id),
        name: this.parseName(person.name),
        telecom: this.parseTelecom(person.telecom),
        address: this.parseAddress(person.addr),
        gender: this.parseCode(person.gender),
        religiousAffiliation: this.parseCode(person.religiousAffiliationCode),
        race: this.parseCode(person.raceCode),
        maritalStatus: this.parseCode(person.maritalStatusCode),
        languageCommunication: this.yieldIfPresent(
          person.languageCommunication,
          (languageCommunication) => ({
            languageCode: this.parseCode(languageCommunication.languageCode),
            modeCode: this.parseCode(languageCommunication.modeCode),
            preferenceInd: this.parseCode(languageCommunication.preferenceInd),
          }),
        ),
        guardian: this.yieldIfPresent(person.guardian, (guardian) => ({
          telecom: this.parseTelecom(guardian.telecom),
          guardianPerson: this.parsePerson(guardian.guardianPerson),
          code: this.parseCode(guardian.code),
          address: this.parseAddress(guardian.addr),
        })),
        ethnicGroup: this.parseCode(person.ethnicGroupCode),
        birthPlace: this.yieldIfPresent(person.birthplace, (birthplace) => ({
          place: this.yieldIfPresent(birthplace.place, (place) => ({
            address: this.parseAddress(place.addr),
          })),
        })),
        birthTime: this.parseTime(person.birthTime),
        administrativeGender: this.parseCode(person.administrativeGenderCode),
      }
    );
  }

  private parseCode(code: AnyJsonObj | undefined): CdaCode | undefined {
    return (
      code && {
        code: code['@code'],
        displayName: code['@displayName'],
        codeSystem: code['@codeSystem'],
        codeSystemName: code['@codeSystemName'],
        originalText: this.parseTextReference(code.originalText),
        translation: this.parseCode(code.translation),
      }
    );
  }

  private parseAddress(address: AnyJsonObj | undefined): CdaAddress | undefined {
    return (
      address && {
        city: this.parseAndFlattenTextReference(address.city),
        country: this.parseAndFlattenTextReference(address.country),
        postalCode: this.parseAndFlattenTextReference(address.postalCode),
        state: this.parseAndFlattenTextReference(address.state),
        streetAddressLine: this.parseAndFlattenTextReference(address.streetAddressLine),
      }
    );
  }

  private parseName(name: AnyJsonObj | undefined): CdaHumanName | undefined {
    return (
      name && {
        family: this.parseAndFlattenTextReference(name.family),
        given: this.parseAndFlattenTextReference(name.given),
        prefix: this.parseAndFlattenTextReference(name.prefix),
        suffix: this.parseAndFlattenTextReference(name.suffix),
        use: this.parseAndFlattenTextReference(name.use),
        text: this.parseAndFlattenTextReference(name.text),
      }
    );
  }

  private parseId(id: AnyJsonObj | undefined): CdaId | undefined {
    return (
      id && {
        extension: id['@extension'],
        root: id['@root'],
      }
    );
  }

  private parseOrganization(organization: AnyJsonObj | undefined): CdaOrganization | undefined {
    return (
      organization && {
        id: this.parseId(organization.id),
        name: this.parseAndFlattenTextReference(organization.name),
        telecom: this.parseTelecom(organization.telecom),
        address: this.parseAddress(organization.addr),
      }
    );
  }

  private parseDoseQuantity(doseQuantity: AnyJsonObj | undefined): CdaDoseQuantity | undefined {
    return {
      numerator: this.parseQuantity(doseQuantity?.numerator),
      denominator: this.parseQuantity(doseQuantity?.denominator),
    };
  }

  /* This commented block parses data we don't currently use.
   * Until we need it, it is commented out to avoid unnecessary
   * time and memory usage when parsing.
   *
  private parseAuthor(author: AnyJsonObj | undefined): CdaAuthor | undefined {
    return (
      author && {
        time: this.parseTime(author.time),
        assignedAuthor: this.parseEntity(author.assignedAuthor ?? author),
      }
    );
  }
  */

  private parseQuantity(value: AnyJsonObj | undefined): CdaQuantity | undefined {
    return (
      value && {
        xsiType: value['@xsi:type'],
        code: this.parseCode(value.code),
        value: value['@value'],
        unit: value['@unit'],
        low: this.yieldIfPresent(value.low, (low) => ({
          value: low['@value'],
          unit: low['@unit'],
        })),
        high: this.yieldIfPresent(value.high, (high) => ({
          value: high['@value'],
          unit: high['@unit'],
        })),
      }
    );
  }

  private parseTelecom(telecom: AnyJsonObj | undefined): CdaTelecom | undefined {
    return (
      telecom && {
        value: telecom?.['@value'],
        use: telecom?.['@use'],
      }
    );
  }

  private parseAndFlattenTextReference(text: AnyJsonObj | undefined): string | undefined {
    return text?.['#text'];
  }

  private parseTextReference(text: AnyJsonObj | undefined): CdaTextReference | undefined {
    return (
      text && {
        text: text['#text'],
        reference: text.reference?.['@value'],
      }
    );
  }

  private parseTime(time: AnyJsonObj | undefined): CdaTime | undefined {
    return (
      time && {
        value: time['@value'],
        high: time.high?.['@value'],
        low: time.low?.['@value'],
      }
    );
  }

  private yieldArray(
    array: AnyJsonObj | Array<AnyJsonObj>,
    parseArray: (array: Array<NonNullable<AnyJsonObj>>) => Array<AnyJsonObj | undefined>,
  ): Array<NonNullable<AnyJsonObj>> {
    if (Array.isArray(array)) {
      return array.map((item) => {
        if (Array.isArray(item)) {
          return compact(parseArray(compact(array)));
        }

        return compact(parseArray(compact([item])))?.[0];
      });
    }

    return compact(parseArray(compact([array])));
  }

  private yieldIfPresent(
    object: AnyJsonObj | undefined,
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    parseObject: (object: AnyJsonObj) => any,
  ) {
    return object ? parseObject(object) : undefined;
  }

  private recursivelyCompactObject(object: AnyJsonObj): AnyJsonObj {
    const isArray = Array.isArray(object);
    for (const key in object) {
      if (object[key] === undefined) {
        isArray ? object.splice(key as never, 1) : delete object[key];
      } else if (typeof object[key] === 'object') {
        this.recursivelyCompactObject(object[key]);
      }
    }

    return object;
  }

  private xmlToJson(node: Node): AnyJsonObj | undefined {
    if (node.nodeName === 'text' && node.nodeType === Node.ELEMENT_NODE) {
      return (node as Element).innerHTML as never;
    }

    // Handle text nodes
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue?.trim();
      return text ? { '#text': text } : undefined;
    }

    // Handle element nodes
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      // biome-ignore lint/suspicious/noExplicitAny: <We don't know what it is yet>
      const result: Record<string, any> = {};

      // Add attributes
      for (const attr of element.attributes) {
        result[`@${attr.name}`] = attr.value;
      }

      // Process child nodes
      const childNodes = Array.from(element.childNodes);
      // Remove undefined values
      const childResults = childNodes.map((el) => this.xmlToJson(el)).filter(Boolean);

      // Handle child nodes based on their structure
      if (childResults.length === 0) {
        // No children, leave as empty object
      } else if (childResults.length === 1 && childResults[0]?.['#text']) {
        // Single text node child
        result['#text'] = childResults[0]['#text'];
      } else {
        // Multiple children or complex children
        // biome-ignore lint/suspicious/noExplicitAny: <Type isn't determined yet>
        const childrenByNodeName: Record<string, Array<any>> = {};

        for (const childNode of childNodes) {
          if (childNode.nodeType === Node.ELEMENT_NODE) {
            const nodeName = childNode.nodeName;
            if (!childrenByNodeName[nodeName]) {
              childrenByNodeName[nodeName] = [];
            }

            const childResult = this.xmlToJson(childNode);
            if (childResult) {
              childrenByNodeName[nodeName].push(childResult);
            }
          } else if (childNode.nodeType === Node.TEXT_NODE) {
            const text = childNode.nodeValue?.trim();
            if (text) {
              result['#text'] = text;
            }
          }
        }

        // Add children to result
        for (const [nodeName, values] of Object.entries(childrenByNodeName)) {
          if (values.length === 1) {
            result[nodeName] = values[0];
          } else {
            result[nodeName] = values;
          }
        }
      }

      return result;
    }

    return undefined;
  }
}
